import {useState, useEffect} from 'react';
import handleError from '../../../../../lib/error';
import type NetworkOptimizationStore from '../../../lib/store';

interface UseDisruptionAnalysisDataLoadingParams {
    networkOptimizationStore: NetworkOptimizationStore;
}

interface UseDisruptionAnalysisDataLoadingReturn {
    loading: boolean;
    error: string | null;
}

/**
 * Hook to manage disruption analysis data loading
 * Loads data when activeTab === 3 and filters change
 */
export function useDisruptionAnalysisDataLoading({
    networkOptimizationStore
}: UseDisruptionAnalysisDataLoadingParams): UseDisruptionAnalysisDataLoadingReturn {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        if (!networkOptimizationStore.disruptionAnalysisSpecialtyFilter) {
            setLoading(false);
            setError('Please select a specialty to view providers.');
            return;
        }

        setLoading(true);
        setError(null);

        const loadData = async () => {
            try {
                await networkOptimizationStore.loadDisruptionAnalysisLocalProviders(controller);
                networkOptimizationStore.deselectAllAdditionalProviders();
                networkOptimizationStore.selectAllExistingProviders();
                setLoading(false);
            } catch (err) {
                handleError(err as Error, setError);
            }
        };

        // Only load when activeTab is 3 (Disruption Analysis tab)
        if (networkOptimizationStore.activeTab === 3) {
            loadData();
        }

        return () => controller.abort();
    }, [
        networkOptimizationStore.disruptionAnalysisSpecialtyFilter,
        networkOptimizationStore.activeTab,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.state,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.county,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.zip,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
    ]);

    return {
        loading,
        error
    };
}
